// Source-of-truth list of US driver's licence issuing states accepted by
// the Samba Safety MVR API. The 50 states plus DC. Ordered alphabetically
// by state name for UI display.
//
// Imported by:
//  - artifacts/api-server/src/lib/samba-safety.ts (server-side validation)
//  - artifacts/bookaclassic (issuing-state dropdown at checkout)

export interface UsState {
  /** USPS 2-letter code, uppercase. This is the value stored & sent to MVR. */
  code: string;
  /** Full state name, e.g. "California". */
  name: string;
}

export const US_STATES: readonly UsState[] = [
  { code: "AL", name: "Alabama" },
  { code: "AK", name: "Alaska" },
  { code: "AZ", name: "Arizona" },
  { code: "AR", name: "Arkansas" },
  { code: "CA", name: "California" },
  { code: "CO", name: "Colorado" },
  { code: "CT", name: "Connecticut" },
  { code: "DE", name: "Delaware" },
  { code: "DC", name: "District of Columbia" },
  { code: "FL", name: "Florida" },
  { code: "GA", name: "Georgia" },
  { code: "HI", name: "Hawaii" },
  { code: "ID", name: "Idaho" },
  { code: "IL", name: "Illinois" },
  { code: "IN", name: "Indiana" },
  { code: "IA", name: "Iowa" },
  { code: "KS", name: "Kansas" },
  { code: "KY", name: "Kentucky" },
  { code: "LA", name: "Louisiana" },
  { code: "ME", name: "Maine" },
  { code: "MD", name: "Maryland" },
  { code: "MA", name: "Massachusetts" },
  { code: "MI", name: "Michigan" },
  { code: "MN", name: "Minnesota" },
  { code: "MS", name: "Mississippi" },
  { code: "MO", name: "Missouri" },
  { code: "MT", name: "Montana" },
  { code: "NE", name: "Nebraska" },
  { code: "NV", name: "Nevada" },
  { code: "NH", name: "New Hampshire" },
  { code: "NJ", name: "New Jersey" },
  { code: "NM", name: "New Mexico" },
  { code: "NY", name: "New York" },
  { code: "NC", name: "North Carolina" },
  { code: "ND", name: "North Dakota" },
  { code: "OH", name: "Ohio" },
  { code: "OK", name: "Oklahoma" },
  { code: "OR", name: "Oregon" },
  { code: "PA", name: "Pennsylvania" },
  { code: "RI", name: "Rhode Island" },
  { code: "SC", name: "South Carolina" },
  { code: "SD", name: "South Dakota" },
  { code: "TN", name: "Tennessee" },
  { code: "TX", name: "Texas" },
  { code: "UT", name: "Utah" },
  { code: "VT", name: "Vermont" },
  { code: "VA", name: "Virginia" },
  { code: "WA", name: "Washington" },
  { code: "WV", name: "West Virginia" },
  { code: "WI", name: "Wisconsin" },
  { code: "WY", name: "Wyoming" },
] as const;

const US_STATE_CODE_SET = new Set<string>(US_STATES.map((s) => s.code));

/**
 * Normalise an arbitrary licence-state string to the canonical 2-letter
 * uppercase code accepted by the Samba Safety MVR API. Returns `null` if
 * the value is not a valid US state code.
 */
export function normalizeUsStateCode(value: string | null | undefined): string | null {
  if (!value) return null;
  const candidate = value.trim().toUpperCase();
  return US_STATE_CODE_SET.has(candidate) ? candidate : null;
}

/** True when the value (after trim+uppercase) is a recognised US state code. */
export function isUsStateCode(value: string | null | undefined): boolean {
  return normalizeUsStateCode(value) !== null;
}
